import React, { useEffect, useState } from 'react';
import { HandwrittenSignatureField } from './HandwrittenSignatureField';
import { useUserProfile } from '../context/UserProfileContext';
import { splitProfileSignature } from '../utils/userProfile';

function BulletList({ items }) {
  return (
    <ul className="list-disc list-inside space-y-1 text-sm text-gray-300 ml-1">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      {title ? <h4 className="text-sm font-medium text-amber-200/90">{title}</h4> : null}
      {children}
    </div>
  );
}

const CONTROL_METHODS = [
  {
    method: 'Circular saw on table',
    dustSuppression: 'Water',
    workLocation: 'Stone shop',
    respirator: 'N95 respirator',
  },
  {
    method: 'Hand-held circular saw',
    dustSuppression: 'Water',
    workLocation: 'Stone shop',
    respirator: 'Half-face respirator with N100 filters',
  },
  {
    method: 'Hand-held polisher',
    dustSuppression: 'Water',
    workLocation: 'Stone shop',
    respirator: 'Half-face respirator with N100 filters',
  },
  {
    method: 'Circular saw (less than one hour per shift)',
    dustSuppression: 'HEPA vacuum extraction',
    workLocation: 'On-site installation',
    respirator: 'Half-face respirator with N100 filters',
  },
  {
    method: 'Circular saw (more than one hour per shift)',
    dustSuppression: 'HEPA vacuum extraction',
    workLocation: 'On-site installation',
    respirator: 'Full-face respirator with N100 filters',
  },
  {
    method: 'Circular saw',
    dustSuppression: 'None*',
    workLocation: 'Stone shop',
    respirator: 'Full-face (PAPR) respirator with N100 filters',
  },
];

/**
 * ECP for Silica cutting/grinding — content from public/ECP for Silica cutting-grinding.doc
 */
export function EcpSilicaForm({ projectName = '' }) {
  const { name: profileName, signature: profileSignature } = useUserProfile();
  const inputClass =
    'px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent w-full';

  const [name, setName] = useState(profileName || '');
  const [signature, setSignature] = useState(() => {
    const split = splitProfileSignature(profileSignature);
    return { mode: split.image ? 'draw' : 'type', ...split };
  });

  useEffect(() => {
    if (profileName) setName((prev) => prev || profileName);
    if (profileSignature) {
      const split = splitProfileSignature(profileSignature);
      setSignature((prev) => {
        if (prev.text || prev.image) return prev;
        return { mode: split.image ? 'draw' : 'type', ...split };
      });
    }
  }, [profileName, profileSignature]);

  return (
    <form className="space-y-6 w-full">
      <p className="text-lg font-semibold text-white">
        Exposure control plan for cutting, grinding, and polishing stone containing crystalline
        silica (quartz)
      </p>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5 space-y-6">
        <p className="text-sm text-gray-300 leading-relaxed">
          The cutting, grinding, and polishing of stone (e.g., granite) without proper dust controls
          can generate high levels of silica-containing dust. Breathing in this fine dust can cause
          a serious lung disease called silicosis, which is characterized by scarring and thickening
          of the lungs, and can result in death.
        </p>

        <Section title="Protecting workers when cutting, grinding, and polishing silica-containing stone">
          <p className="text-sm text-gray-300 leading-relaxed">
            This document will help employers develop an exposure control plan (ECP) for work
            involving the cutting, grinding, and polishing of stone containing crystalline silica.
          </p>
          <p className="text-sm text-gray-300 leading-relaxed">
            An ECP is a requirement of the Occupational Health and Safety Regulation (the
            Regulation). The ECP sets out a detailed approach to protecting workers from harmful
            exposure to crystalline silica dust.
          </p>
          <p className="text-sm text-gray-300 leading-relaxed">
            Employers can use this document as a template and add specific details regarding safe
            work practices in their operations.
          </p>
          <p className="text-sm text-gray-300 leading-relaxed">
            This document covers the points employers need to follow to protect workers from harmful
            exposure.
          </p>
          <p className="text-sm text-gray-300 leading-relaxed">
            It is important to follow all the points or use equally effective measures.
          </p>
        </Section>

        <Section title="Tools and equipment for cutting, grinding, and polishing stone">
          <p className="text-xs font-medium text-gray-300">Grinders</p>
          <BulletList
            items={[
              'Stationary grinders work well for larger jobs.',
              'Hand-held portable grinders are also used.',
            ]}
          />
          <p className="text-xs font-medium text-gray-300 pt-2">Circular saws</p>
          <BulletList
            items={[
              'A number of equipment manufacturers make saws designed for cutting stone slabs.',
              'These saws are equipped with water attachments for wet-cutting using diamond blades.',
            ]}
          />
          <p className="text-xs font-medium text-gray-300 pt-2">Vacuums</p>
          <BulletList
            items={[
              'Both dry and wet vacs may used. These need to be fitted with HEPA filtration to prevent respirable silica from entering the workplace air.',
            ]}
          />
          <p className="text-xs font-medium text-gray-300 pt-2">Equipment tips</p>
          <BulletList
            items={[
              'Invest in good quality equipment and keep it well maintained.',
              'Well-designed and maintained equipment significantly reduces dust and lasts longer between replacement and maintenance.',
              'A variety of specially designed equipment is available for cutting stone.',
            ]}
          />
        </Section>

        <Section title="Respirator program">
          <BulletList
            items={[
              'Employers whose workers wear respirators are required to develop and implement a respirator protection program.',
              'The WorkSafeBC publication Breathe Safer provides guidance for developing this program.',
            ]}
          />
        </Section>

        <Section title="Hearing conservation">
          <BulletList
            items={[
              'Workers in stone shops are exposed to high levels of noise.',
              'Over time, workers exposed to these noise levels will suffer hearing loss.',
              'Noise hazard signage must be posted in these areas.',
              'Workers must wear appropriate hearing protection in these areas.',
            ]}
          />
        </Section>

        <Section title="Additional information">
          <p className="text-sm text-gray-300 leading-relaxed">
            The following WorkSafeBC publications are available from WorkSafeBC.com:
          </p>
          <BulletList
            items={[
              'Breathe Safer (click Publications)',
              'Toolbox Meeting Guides for Hazardous Materials: Silica (click Safety at Work, then in the Quick Links section click Toolbox Meeting Guides, and then click Hazardous Materials)',
            ]}
          />
          <p className="text-sm text-gray-300 leading-relaxed">
            For more information about health and safety, employers and workers can call the
            WorkSafeBC Prevention Information Line and ask to speak with an occupational hygiene
            officer. Call 604 276-3100 in the Lower Mainland or 1 888 621-7233 toll-free in B.C.
          </p>
        </Section>

        <Section title="Useful links">
          <BulletList
            items={[
              'HSE leaflets on silica and construction can be downloaded free from the HSE web site at www.hse.gov.uk/pubns.',
              'NIOSH publications are available at no charge at www.cdc.gov/niosh (search silica and construction).',
            ]}
          />
        </Section>

        <Section title="Health hazards from silica exposure">
          <BulletList
            items={[
              'Long-term exposure to airborne crystalline silica (e.g., quartz) can cause a disabling, sometimes fatal lung disease called silicosis.',
              'When the dust is inhaled deep into the lungs, microscopic particles of silica can cause scar tissue to form in the lung tissue, which restricts the lungs’ ability to extract oxygen from the air. This damage is permanent, but symptoms of the disease may not appear for many years.',
              'The disease initially causes fatigue and shortness of breath. If exposure continues, it can lead to chest pain, heart problems (difficulty breathing can strain the heart), and respiratory failure.',
              'Exposure to crystalline silica has also been liked to other diseases, including bronchitis, tuberculosis and lung cancer.',
            ]}
          />
        </Section>

        <Section title="Purpose and responsibilities">
          <BulletList
            items={[
              'We have a duty to protect our workers from silica exposure during the manufacture of our stone products. Studies show that work tasks involving the cutting, grinding, and polishing of silica-containing stone products generate airborne silica levels well in excess of safe levels. Effective controls are available to protect workers from harmful exposure.',
              'We know that a combination of control measures will be required to achieve this objective. We commit to being diligent in our efforts to select the most effective control technologies available, and to ensure that the best practices, as described in this exposure control plan (ECP), are followed at our worksites.',
              'The work procedures we establish for cutting, grinding, and polishing silica-containing stone will protect not only our workers but also any other workers on-site who are not involved in these operations.',
            ]}
          />
          <p className="text-xs font-medium text-gray-300 pt-2">
            The employer is responsible for the following:
          </p>
          <BulletList
            items={[
              'Ensure that the materials (i.e., tools, equipment, and personal protective equipment [PPE]) and other resources (i.e., worker training) required to fully implement and maintain this ECP are readily available where and when they are required.',
              'Ensure that all required tools, equipment, and PPE are readily available and used as required by the ECP.',
              'Ensure that supervisors and workers are educated and trained to an acceptable level of competency.',
              'Maintain records of training, fit-test results, crew talks, and inspections (for equipment, PPE, and work methods and practices).',
              'Conduct a periodic review of the effectiveness of the ECP. This includes a review of available dust control technologies to ensure these are selected and used when practical.',
              'Coordinate the work with the prime contractor and other employers to ensure a safe work environment.',
            ]}
          />
          <p className="text-xs font-medium text-gray-300 pt-2">
            Supervisors are responsible for the following:
          </p>
          <BulletList
            items={[
              'Provide adequate instruction to workers on the hazards associated with the cutting, grinding, and polishing of silica-containing stone.',
              'Select and implement the appropriate control measures.',
              'Ensure that workers using respirators have been properly fit-tested and that the results are recorded.',
              'Direct the work in a manner that ensures the risk to workers and others is minimized and adequately controlled. This includes ensuring that workers use appropriate engineering controls and wear the necessary PPE.',
            ]}
          />
          <p className="text-xs font-medium text-gray-300 pt-2">
            Workers are responsible for the following:
          </p>
          <BulletList
            items={[
              'Use the assigned protective equipment in an effective and safe manner.',
              'Follow established work procedures as directed by the supervisor.',
              'Report any unsafe conditions or acts to the supervisor.',
              'Report any exposure incidents or any signs or symptoms of silica illness.',
            ]}
          />
        </Section>

        <Section title="Risk identification and assessment">
          <BulletList
            items={[
              'Stone (e.g., granite) can contain a high percentage of silica.',
              'Cutting, grinding, and polishing stone without the use of proper dust controls and PPE can expose workers to levels of airborne respirable crystalline silica that are above the exposure limit listed in the Occupational Health and Safety Regulation.',
              'Work locations where workers or other persons are exposed to the hazards of silica will be identified with signs, placards, or barrier tape.',
            ]}
          />
        </Section>

        <Section title="Exposure limit">
          <BulletList
            items={[
              'The occupational exposure limit (OEL) for respirable crystalline silica (including quartz) is 0.025 milligrams per cubic metre (mg/m3).',
              'Because crystalline silica is linked to lung cancer, workplace exposures must be reduced to levels that are as low as reasonably achievable (ALARA) below the OEL.',
            ]}
          />
        </Section>

        <Section title="Silica dust control">
          <BulletList
            items={[
              'The Regulation requires employers to select silica dust controls based on the following hierarchy:',
              'Engineering',
              'Administrative controls',
              'Personal protective equipment (PPE)',
              'Use of respirators as a primary control is not acceptable when other methods are available and practical.',
              'Respirators will be used in conjunction with other controls such as wet cutting, grinding, and polishing to reduce worker exposure to silica, unless air monitoring information suggests otherwise.',
              'Wet cutting, grinding, and polishing or the use of local exhaust ventilation (LEV) are the preferred engineering methods and will be used when practicable.',
              'Dry cutting, grinding, and polishing will be avoided unless additional respiratory protection and other controls are used.',
              'Dry cutting or grinding methods might be required for certain applications or work locations. Dry work will be isolated from other work areas. Effective LEV will be used in order to control the dust. A HEPA vacuum will be used for cleanup and decontamination.',
              'Air discharged from an LEV system will not be recirculated into the work area.',
            ]}
          />
        </Section>

        <Section title="Acceptable control methods for cutting, grinding, and polishing stone">
          <BulletList
            items={[
              'The work methods in the following table are acceptable, provided that the respirator selection is adhered to.',
              'The following control options will be used to eliminate or reduce the risk to workers from the hazards of silica dust exposure, unless air monitoring information suggests otherwise:',
            ]}
          />
          <div className="overflow-x-auto border border-gray-800 rounded-lg mt-2">
            <table className="min-w-full text-xs md:text-sm">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-300">
                    Cutting, grinding, and polishing method/tools
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-300">Dust suppression</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-300">Work location</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-300">Respirator type</th>
                </tr>
              </thead>
              <tbody>
                {CONTROL_METHODS.map((row, index) => (
                  <tr
                    key={`${row.method}-${index}`}
                    className={index % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/60'}
                  >
                    <td className="px-3 py-2 text-gray-200 align-top">{row.method}</td>
                    <td className="px-3 py-2 text-gray-200 align-top">{row.dustSuppression}</td>
                    <td className="px-3 py-2 text-gray-200 align-top">{row.workLocation}</td>
                    <td className="px-3 py-2 text-gray-200 align-top">{row.respirator}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-300 leading-relaxed pt-2">
            * This work should be conducted in isolation from the general shop and other workers,
            with some form of general exhaust ventilation.
          </p>
        </Section>

        <Section title="Safe work planning">
          <BulletList
            items={[
              'Select one or more of the methods described in the table above.',
              'Establish a barrier around the work zone to restrict access by unprotected workers.',
              'Inspect all dust control equipment and tools to make sure they are in good working order.',
              'Use and maintain all tools as specified by the manufacturer.',
              'When working off-site, provide the general contractor with a copy of the silica exposure control plan and safe work procedures.',
              'Ensure that workers inspect their respirators before start-up.',
              'Monitor dust release from equipment during use. When tools and equipment are working properly, very little dust should be visible in the air. Stop work if excessive dust is observed.',
            ]}
          />
        </Section>

        <Section title="Respiratory protective equipment">
          <BulletList
            items={[
              'Each worker will be fit-tested if a respirator is required.',
              'When the worker notices a notable resistance to breathing, the respirator filters must be replaced.',
              'Respirators will be used, cleaned, and stored in accordance with the respiratory protection program.',
            ]}
          />
        </Section>

        <Section title="Other personal protective equipment and hygiene">
          <BulletList
            items={[
              'Workers will wear approved safety glasses and hearing protection when cutting silica-containing stone. This equipment will not interfere with the fit of the worker’s respirator.',
              'Workers will wear washable work coveralls that do not retain dust. These coveralls will be laundered and changed regularly, and workers will not wear them outside the work area.',
            ]}
          />
        </Section>

        <Section title="Housekeeping procedures">
          <BulletList
            items={[
              'Dry sweeping and the use of compressed air are prohibited for removing dust and debris containing silica. Work areas and equipment covered by dust will be cleaned at the end of every shift using a HEPA filter vacuum.',
              'Wet cleanup may also be used to remove dust.',
              'Waste material will be placed in a dumpster and will be removed at least weekly. The location and method used to store waste will not allow silica-containing dust to re-enter the workplace.',
              'Supervisors are responsible for ensuring that work areas are free from dust at the end of each shift.',
            ]}
          />
        </Section>

        <Section title="Worker training for silica dust exposure">
          <BulletList
            items={[
              'Training will be performed by the employer or the employer’s designate.',
              'Records of attendance, dates of training, and training material will be documented and retained at the stone shop.',
              'Additional training or reference material on silica dust exposure will be made available to employees upon request.',
            ]}
          />
        </Section>

        <Section title="Training topics">
          <BulletList
            items={[
              'Health hazards of silica dust exposure (including signs and symptoms of silicosis)',
              'Operations and materials that can produce silica dust exposures',
              'Engineering controls and safe work practices used to protect workers',
              'The importance of proper equipment control and maintenance',
              'Housekeeping procedures',
              'Proper use of respirators and the respirator program',
              'Personal hygiene procedures to reduce exposures',
              'How smoking increases the risk of developing silicosis and other lung damage',
              'The details of the exposure control program for silica dust',
            ]}
          />
        </Section>

        <Section title="Health surveillance">
          <BulletList
            items={[
              'Workers who are regularly exposed to silica dust will receive regular medical examinations from their family physicians. These examinations may include chest x-rays.',
              'Workers will report any symptoms of silica exposure to the employer and WorkSafeBC, for tracking and investigation.',
            ]}
          />
        </Section>

        <Section title="Annual review">
          <p className="text-sm text-gray-300 leading-relaxed">
            This ECP will be reviewed at least annually and updated as necessary by the employer, in
            consultation with the workplace health and safety committee or the worker health and
            safety representative.
          </p>
        </Section>

        <div className="pt-4 border-t border-gray-800 space-y-4">
          <h4 className="text-sm font-medium text-white">Name &amp; Sign</h4>
          <div className="grid gap-4 md:grid-cols-2 max-w-2xl">
            <div>
              <label className="text-xs font-medium text-gray-300 mb-1 block">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
                placeholder="Name"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-300 mb-1 block">Sign</label>
              <HandwrittenSignatureField
                textValue={signature.text}
                imageValue={signature.image}
                onTextChange={(value) => setSignature((p) => ({ ...p, text: value }))}
                onImageChange={(value) => setSignature((p) => ({ ...p, image: value }))}
                placeholder="Type signature"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
